import React from 'react';

const CashSummaryPanel = ({
  cashActionsData,
  isLoadingRebalance,
  rebalanceError,
  accountCashLevels,
  accountMapping,
  isLoadingCashLevels,
  isLoadingCashActions,
  formatCurrency
}) => {
  if (!cashActionsData || isLoadingRebalance || rebalanceError) return null;

  // Check if any accountCashLevels has trades
  const hasAnyTrades = Object.values(accountCashLevels).some(cashLevel => 
    cashLevel && (cashLevel.trades_count > 0 || cashLevel.estimated_cash_impact !== 0)
  );

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Current Cash by Account</h6>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        {Object.keys(accountCashLevels).length > 0 && hasAnyTrades && (
          <span className="badge bg-warning">
            <i className="bi bi-exclamation-triangle me-1"></i>
            Trades Affecting Cash
          </span>
        )}
      </div>
      {/* Single column layout with multiple rows */}
      {cashActionsData.map((account, index) => {
        const accountName = account['Account Name'] || account.accountName || 'Unknown Account';
        const accountNumber = account['Account Number'] || account.accountNumber || '';
        const originalCash = account['Holdings Current Value'] || account.holdingsCurrentValue || 0;
        const reserveCenter = account['Calculated Reserve Center'] || account.calculatedReserveCenter || 0;
        const investableCash = originalCash - reserveCenter || 0;
        const minReserve = account['Calculated Reserve Min'] || 0;
        const maxReserve = account['Calculated Reserve Max'] || 0;
        const originalCashAction = account['Cash Action'] || account.cashAction || 'Neutral';

        // Check if we have updated cash levels for this account
        // Use accountMapping to get the correct account number for lookup
        const lookupAccountNumber = accountMapping[accountName] || accountNumber;
        const updatedCashInfo = accountCashLevels[lookupAccountNumber];
        const hasUpdates = updatedCashInfo && updatedCashInfo.trades_count !== undefined && updatedCashInfo.trades_count > 0;

        // Use updated cash levels if available, otherwise use original
        const currentCash = hasUpdates ? updatedCashInfo.new_cash_level : originalCash;
        const currentInvesteableCash = hasUpdates ? updatedCashInfo.new_cash_level - reserveCenter : investableCash;
        const cashAction = hasUpdates ? updatedCashInfo.new_cash_action : originalCashAction;
        const estimatedImpact = hasUpdates ? updatedCashInfo.estimated_cash_impact : 0;

        // Determine cash status styling
        let cashStatusClass = '';
        let statusText = '';
        if (currentCash < minReserve) {
          cashStatusClass = hasUpdates ? 'border-warning' : 'border-danger';
          statusText = 'Below Min';
        } else if (currentCash > maxReserve) {
          cashStatusClass = hasUpdates ? 'border-info' : 'border-success';
          statusText = 'Above Max';
        } else {
          cashStatusClass = hasUpdates ? 'border-primary' : 'border-secondary';
          statusText = 'In Range';
        }

        return (
          <div key={index} className="col mb-2">
            <div className={`card h-100 ${cashStatusClass} ${hasUpdates ? 'shadow-sm' : ''}`} style={{ minHeight: '100px', position: 'relative' }}>
              {/* Loading indicator */}
              {(isLoadingRebalance || isLoadingCashActions) && (
                <div className="position-absolute top-0 end-0 p-1">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: '12px', height: '12px' }}>
                    <span className="visually-hidden">Updating...</span>
                  </div>
                </div>
              )}
              {/* Update indicator */}
              {hasUpdates && !(isLoadingRebalance || isLoadingCashActions) && (
                <div className="position-absolute top-0 end-0 p-1">
                  <i className="bi bi-arrow-clockwise text-primary" title="Updated based on trades"></i>
                </div>
              )}
              <div className="card-body text-center p-1" style={{ fontSize: '0.75rem', color: hasUpdates ? 'inherit' : 'black' }}>
                <div className="mb-1" style={{ fontSize: '0.7rem', fontWeight: '600', lineHeight: '1.1' }}>
                  {accountName}
                  {accountNumber && <div className="text-muted" style={{ fontSize: '0.65rem' }}>{accountNumber}</div>}
                </div>
                {/* Cash amount with change indicator */}
                <div className="mb-1">
                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }} className={hasUpdates ? 'text-primary' : ''}>
                    Total Cash: {formatCurrency(currentCash)}
                  </div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }} className={hasUpdates ? 'text-primary' : ''}>
                    Investable Cash: {formatCurrency(currentInvesteableCash)}
                  </div>
                  {hasUpdates && estimatedImpact !== 0 && (
                    <div style={{ fontSize: '0.65rem' }} className={estimatedImpact < 0 ? 'text-danger' : 'text-success'}>
                      {estimatedImpact < 0 ? '−' : '+'}{formatCurrency(Math.abs(estimatedImpact))}
                      {updatedCashInfo.trades_count && (
                        <span className="text-muted"> ({updatedCashInfo.trades_count})</span>
                      )}
                    </div>
                  )}
                </div>
                {/* Reserve range */}
                <div className="mb-1" style={{ fontSize: '0.6rem', lineHeight: '1.0' }}>
                  <div className="text-muted">
                    {formatCurrency(minReserve)} --- {formatCurrency(reserveCenter)} --- {formatCurrency(maxReserve)}
                  </div>
                </div>
                {/* Status badge */}
                <div>
                  <span className={`badge ${cashAction === 'Raise Cash' ? 'bg-danger' :
                    cashAction === 'Invest Cash' ? 'bg-success' : 'bg-secondary'
                    } ${hasUpdates ? 'border border-white' : ''}`} style={{ fontSize: '0.6rem', padding: '1px 4px' }}>
                    {statusText}
                    {hasUpdates && originalCashAction !== cashAction && (
                      <i className="bi bi-arrow-right ms-1" title={`Changed from ${originalCashAction}`}></i>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CashSummaryPanel;
